import { useCustom } from "@refinedev/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { PublicationStatusResponse } from "pages/media/types";
import { useWsSubscription } from "hooks/useWsSubscription";
import { NOTIFICATIONS_EVENT_TYPE, WSEvent } from "../../../../store/websocketStore";

// Reads the instance's publication state (separate from the media/video's own
// generation status, see MED-6885) and keeps it fresh via the
// `PublicationStatusChanged` websocket event instead of polling.
export const usePublicationStatus = ({
  projectId,
  instanceId,
}: {
  projectId?: string;
  instanceId?: string;
}) => {
  const {
    data,
    isLoading,
    refetch,
  } = useCustom<PublicationStatusResponse>({
    url: `media/projects/${projectId}/instances/${instanceId}/status`,
    method: "get",
    queryOptions: {
      enabled: Boolean(projectId) && Boolean(instanceId),
    },
    errorNotification: false,
  });

  // Last known live state, used by the WS handler to detect a genuine
  // false -> true transition (mirrors the AddOnStatusChanged pattern in
  // useAddOnStatuses). Seeded from fetched data, not just WS events, so the
  // baseline is right even before the first event arrives.
  const wasLiveRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (data?.data) wasLiveRef.current = Boolean(data.data.has_live_publication);
  }, [data]);

  const [publishedToastId, setPublishedToastId] = useState<number | null>(
    null
  );
  const toastIdRef = useRef(0);

  const handler = useCallback(
    (event: WSEvent) => {
      if (event.event !== NOTIFICATIONS_EVENT_TYPE.PublicationStatusChanged)
        return;
      if (event.instance_id !== instanceId) return;

      const nowLive = Boolean(event.has_live_publication);
      const wasLive = wasLiveRef.current;
      wasLiveRef.current = nowLive;
      if (nowLive && wasLive === false) {
        toastIdRef.current += 1;
        setPublishedToastId(toastIdRef.current);
      }

      void refetch();
    },
    [instanceId, refetch]
  );

  useWsSubscription({ type: "project", id: projectId }, handler);

  const dismissPublishedToast = useCallback(() => {
    setPublishedToastId(null);
  }, []);

  return {
    publicationStatus: data?.data,
    isLoadingPublicationStatus: isLoading,
    refetchPublicationStatus: refetch,
    publishedToastId,
    dismissPublishedToast,
  };
};
